import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap } from '@cm/core/localization';

const pathStart = 'Candidate|History|Credential Requests|Grant Credential Modal|';
const sharedProperties: Pick<LocDef, 'shared' | 'feature' | 'portal'> = {
	shared: false,
	feature: HubFeature.Credential,
	portal: 'SP',
};

type LocNameStart = 'SPCandidateHistoryGrantCredential';
export type SPCandidateHistoryGrantCredentialLocNames =
	| `${LocNameStart}TitleModal`
	| `${LocNameStart}ContentInstructions`
	| `${LocNameStart}LabelCredential`
	| `${LocNameStart}ErrorCredentialRequired`
	| `${LocNameStart}ErrorCredentialNoMatch`
	| `${LocNameStart}LabelActiveDate`
	| `${LocNameStart}LabelStatus`
	| `${LocNameStart}LabelStatusDefault`
	| `${LocNameStart}ErrorStatusRequired`
	| `${LocNameStart}ErrorStatusNoMatch`
	| `${LocNameStart}ErrorActiveDateRequired`
	| `${LocNameStart}ErrorActiveDateMin`
	| `${LocNameStart}LabelComments`
	| `${LocNameStart}ErrorCommentsRequired`
	| `${LocNameStart}ButtonCancel`
	| `${LocNameStart}ButtonContinue`;

export const spCandidateHistoryGrantCredentialLocs: LocDefs<SPCandidateHistoryGrantCredentialLocNames> =
	{
		SPCandidateHistoryGrantCredentialTitleModal: {
			defaultValue: 'Grant A Credential',
			description: 'Modal Title',
			displayPath: `${pathStart}Modal Title`,
			displayType: 'Title',
			legacyName: 'Credential/GrantCredentialModal.Title.GrantCredential',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialContentInstructions: {
			defaultValue: `
            <p>
			Choose the credential to grant and the date on which the instance should be granted. The
			expire/inactive dates will be automatically calculated.
		</p>
		<br />
		<p>
			The system will only allow your request if the credential was active on the date you
			specify and does not overlap with an existing instance. (i.e., you can't grant a
			credential instance on a date after which the credential was retired, and a candidate
			cannot have more than one certification instance in play at the same time.)
		</p>`,
			description: 'Modal Content Instructions',
			displayPath: `${pathStart}Modal Content Instructions`,
			displayType: 'Content',
			legacyName: 'Credential/GrantCredentialModal.Content.Instructions',
			htmlAllowed: true,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialLabelCredential: {
			defaultValue: 'Credential',
			description: 'Field Label for Credential',
			displayPath: `${pathStart}Credential`,
			displayType: 'Label',
			legacyName: 'Credential/GrantCredentialModal.FieldLabel.Credential',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialErrorCredentialRequired: {
			defaultValue: 'This field is required',
			description: 'Field Error for Credential Required',
			displayPath: `${pathStart}Credential Required`,
			displayType: 'Error',
			legacyName: 'Credential/GrantCredentialModal.FieldError.CredentialRequired',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialErrorCredentialNoMatch: {
			defaultValue: 'You must choose an option from the list',
			description: 'Field Error for Credential No Match',
			displayPath: `${pathStart}Credential No Match`,
			displayType: 'Error',
			legacyName: 'Credential/GrantCredentialModal.FieldError.CredentialNoMatch',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialLabelActiveDate: {
			defaultValue: 'Active Date',
			description: 'Field Label for Active Date',
			displayPath: `${pathStart}Active Date`,
			displayType: 'Label',
			legacyName: 'Credential/GrantCredentialModal.FieldLabel.ActiveDate',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialErrorActiveDateRequired: {
			defaultValue: 'This field is required',
			description: 'Field Error for Active Date Required',
			displayPath: `${pathStart}Active Date Required`,
			displayType: 'Error',
			legacyName: 'Credential/GrantCredentialModal.FieldError.ActiveDateRequired',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialErrorActiveDateMin: {
			defaultValue: 'Effective date must be later than the catalog active date',
			description: 'Field Error for Active Date Min',
			displayPath: `${pathStart}Active Date Min Date`,
			displayType: 'Error',
			legacyName: 'Credential/GrantCredentialModal.FieldError.ActiveDateMin',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialLabelStatus: {
			defaultValue: 'Status',
			description: 'Field Label for Status',
			displayPath: `${pathStart}Status`,
			displayType: 'Label',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialLabelStatusDefault: {
			defaultValue: 'Default',
			description: 'Label for the default cert status.',
			displayPath: `${pathStart}Status Default`,
			displayType: 'Label',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialErrorStatusRequired: {
			defaultValue: 'This field is required',
			description: 'Field Error for Status Required',
			displayPath: `${pathStart}Status Required`,
			displayType: 'Error',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialErrorStatusNoMatch: {
			defaultValue: 'You must choose an option from the list',
			description: 'Field Error for Status No Match',
			displayPath: `${pathStart}Status No Match`,
			displayType: 'Error',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialLabelComments: {
			defaultValue: 'Comments',
			description: 'Field Label for Comments',
			displayPath: `${pathStart}Comments`,
			displayType: 'Label',
			legacyName: 'Credential/GrantCredentialModal.FieldLabel.Comments',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialErrorCommentsRequired: {
			defaultValue: 'This field is required',
			description: 'Field Error for Comments Required',
			displayPath: `${pathStart}Comments Required`,
			displayType: 'Error',
			legacyName: 'Credential/GrantCredentialModal.FieldError.CommentsRequired',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialButtonCancel: {
			defaultValue: 'Cancel',
			description: 'Button Cancel',
			displayPath: `${pathStart}Button Cancel`,
			displayType: 'Button',
			legacyName: 'Credential/GrantCredential.Modal.Button.Cancel',
			htmlAllowed: false,
			...sharedProperties,
		},
		SPCandidateHistoryGrantCredentialButtonContinue: {
			defaultValue: 'Continue',
			description: 'Button Continue',
			displayPath: `${pathStart}Button Continue`,
			displayType: 'Button',
			legacyName: 'Credential/GrantCredential.Modal.Button.Continue',
			htmlAllowed: false,
			...sharedProperties,
		},
	};

export type SPCandidateHistoryGrantCredentialLocType =
	LocMap<SPCandidateHistoryGrantCredentialLocNames>;
